/** Org cascade field tracked in `org_override_fields` (EVI-1125). */
export const ORG_OVERRIDE_TEAM_EVIDENCE_SETTINGS = 'team_evidence_settings';

type ReadOnlyFieldSlice = {
  field_name?: string | null;
};

/** Team GET returns `readonly_fields`; SimpleTeam uses `org_override_fields`. */
export function isTeamEvidenceSettingsOverriddenByOrg(
  orgOverrideFields: string[] | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): boolean {
  if ((orgOverrideFields ?? []).includes(ORG_OVERRIDE_TEAM_EVIDENCE_SETTINGS)) {
    return true;
  }

  return (readonlyFields ?? []).some(
    (field) => field.field_name === ORG_OVERRIDE_TEAM_EVIDENCE_SETTINGS
  );
}

/** Phase-1 org-cascaded source-control fields from the team API.
 *
 * The four `*_enabled` toggles are tri-state (`boolean | null`) on team configs
 * after [ml-scribe#15788](https://github.com/oscerai/ml-scribe/pull/15788):
 * `null` means the team has never set the toggle (inherit the org floor);
 * `false` means the team explicitly disabled it (respect it); `true` means
 * explicitly enabled. Org configs always carry explicit booleans. Before the
 * BE PR deploys, the field is always `true` (never `null`), so widening to
 * `boolean | null` is forward-compatible. */
export type TeamSourceControlConfigSlice = {
  local_guidelines_enabled?: boolean | null;
  local_guidelines_changeable?: boolean;
  primary_evidence_enabled?: boolean | null;
  primary_evidence_changeable?: boolean;
  web_collections_enabled?: boolean | null;
  web_collections_changeable?: boolean;
  integration_partners_enabled?: boolean | null;
  integration_partners_changeable?: boolean;
};

export type OrgEvidenceSettingsSlice = {
  evidence_country?: string | null;
  evidence_jurisdiction?: string | null;
  enforce_evidence_country?: boolean;
  enforce_evidence_jurisdiction?: boolean;
  chat_retention_enabled?: boolean;
  chat_retention_days?: number | null;
  enforce_chat_retention?: boolean;
};

export type OrgEvidenceSourceLockState = 'none' | 'disabled' | 'forced_on' | 'managed';

function resolveOrgSourceLockState(
  isOrgOverridden: boolean,
  enabled: boolean,
  changeable: boolean
): OrgEvidenceSourceLockState {
  if (!isOrgOverridden) {
    return 'none';
  }
  if (!enabled) {
    return 'disabled';
  }
  if (!changeable) {
    return 'forced_on';
  }
  return 'managed';
}

function isOrgEvidenceOverridden(
  orgOverrideFields: string[] | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): boolean {
  return isTeamEvidenceSettingsOverriddenByOrg(orgOverrideFields, readonlyFields);
}

export function getOrgClinicalSourcesLockState(
  orgOverrideFields: string[] | null | undefined,
  sourceControlConfig: TeamSourceControlConfigSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  if (
    !isOrgEvidenceOverridden(orgOverrideFields, readonlyFields) ||
    !sourceControlConfig
  ) {
    return 'none';
  }

  const enabled =
    (sourceControlConfig.local_guidelines_enabled ?? true) &&
    (sourceControlConfig.primary_evidence_enabled ?? true);
  const changeable =
    (sourceControlConfig.local_guidelines_changeable ?? true) &&
    (sourceControlConfig.primary_evidence_changeable ?? true);

  return resolveOrgSourceLockState(true, enabled, changeable);
}

export function getOrgClinicalWebLockState(
  orgOverrideFields: string[] | null | undefined,
  sourceControlConfig: TeamSourceControlConfigSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  if (
    !isOrgEvidenceOverridden(orgOverrideFields, readonlyFields) ||
    !sourceControlConfig
  ) {
    return 'none';
  }

  const enabled = sourceControlConfig.web_collections_enabled ?? true;
  const changeable = sourceControlConfig.web_collections_changeable ?? true;

  return resolveOrgSourceLockState(true, enabled, changeable);
}

export function getOrgIntegrationPartnersLockState(
  orgOverrideFields: string[] | null | undefined,
  sourceControlConfig: TeamSourceControlConfigSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  if (
    !isOrgEvidenceOverridden(orgOverrideFields, readonlyFields) ||
    !sourceControlConfig
  ) {
    return 'none';
  }

  const enabled = sourceControlConfig.integration_partners_enabled ?? true;
  const changeable = sourceControlConfig.integration_partners_changeable ?? true;

  return resolveOrgSourceLockState(true, enabled, changeable);
}

function getOrgRegionalFieldLockState(
  orgOverrideFields: string[] | null | undefined,
  readonlyFields: ReadOnlyFieldSlice[] | null | undefined,
  value: string | null | undefined,
  enforce: boolean | undefined
): OrgEvidenceSourceLockState {
  if (!isOrgEvidenceOverridden(orgOverrideFields, readonlyFields) || !value) {
    return 'none';
  }
  if (enforce) {
    return 'forced_on';
  }
  return 'managed';
}

export function getOrgRegionalCountryLockState(
  orgOverrideFields: string[] | null | undefined,
  orgEvidenceSettings: OrgEvidenceSettingsSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  return getOrgRegionalFieldLockState(
    orgOverrideFields,
    readonlyFields,
    orgEvidenceSettings?.evidence_country ?? undefined,
    orgEvidenceSettings?.enforce_evidence_country
  );
}

export function getOrgRegionalJurisdictionLockState(
  orgOverrideFields: string[] | null | undefined,
  orgEvidenceSettings: OrgEvidenceSettingsSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  return getOrgRegionalFieldLockState(
    orgOverrideFields,
    readonlyFields,
    orgEvidenceSettings?.evidence_jurisdiction ?? undefined,
    orgEvidenceSettings?.enforce_evidence_jurisdiction
  );
}

export function getOrgChatRetentionLockState(
  orgOverrideFields: string[] | null | undefined,
  orgEvidenceSettings: OrgEvidenceSettingsSlice | null | undefined,
  readonlyFields?: ReadOnlyFieldSlice[] | null
): OrgEvidenceSourceLockState {
  if (!isOrgEvidenceOverridden(orgOverrideFields, readonlyFields)) {
    return 'none';
  }

  // Mirror regional: only lock when org explicitly enforces. Org OFF without
  // enforce leaves teams free (EVI-2121). Org ON without enforce is managed.
  if (!orgEvidenceSettings?.enforce_chat_retention) {
    if (orgEvidenceSettings?.chat_retention_enabled) {
      return 'managed';
    }
    return 'none';
  }

  if (!orgEvidenceSettings.chat_retention_enabled) {
    return 'disabled';
  }

  return 'forced_on';
}

/**
 * Maps an org source lock state to the in-session enforcement directive.
 *
 * - `forced_on` → org mandated the source: ON and locked (team can't opt out)
 * - `disabled`  → org disabled the source: hidden (team can't override)
 * - `managed`   → org provided a default: the source is editable in-session,
 *                 but `enabled` is NOT forced — the team's own
 *                 `source_control_config` decides whether it's on by default
 *                 (a team admin may have toggled it off on the Settings page;
 *                 `isOrgValuePinned('managed')` is false, so the toggle stays
 *                 editable there). The org only guarantees `changeable=true`.
 * - `none`      → `null` — org hasn't overridden; caller falls back to team config
 */
export type OrgSourceDirective =
  | { kind: 'forced_on' }
  | { kind: 'disabled' }
  | { kind: 'managed' }
  | null;

export function orgLockStateToDirective(
  lockState: OrgEvidenceSourceLockState
): OrgSourceDirective {
  switch (lockState) {
    case 'forced_on':
      return { kind: 'forced_on' };
    case 'disabled':
      return { kind: 'disabled' };
    case 'managed':
      return { kind: 'managed' };
    default:
      return null;
  }
}

export function isOrgFieldLocked(orgLockState: OrgEvidenceSourceLockState): boolean {
  return orgLockState !== 'none';
}

/**
 * Resolve a single source toggle's enabled state under the tri-state contract.
 *
 * Used by both the Settings form (`useTeamEvidenceSettingsForm`) and the
 * in-session enforcement hook (`useTeamEvidenceEnforcement`) so the two
 * surfaces agree on mixed tri-state values.
 *
 * - `managed` + team `null` (never set) → org floor (fail-open to `true`),
 *   so a team that never touched the toggle inherits the org default.
 * - `managed` + team `false` (explicit opt-out) → `false` (EVI-2116
 *   granularity: a team admin's saved OFF persists).
 * - `managed` + team `true` → `true`.
 * - non-`managed` (org `none`/`forced_on`/`disabled`) → the team's own value
 *   applies (fail-open to `true`); `forced_on`/`disabled` are pinned elsewhere
 *   and already written into the team config by the backend.
 */
export function resolveManagedEnabled(
  orgLockState: OrgEvidenceSourceLockState,
  teamEnabled: boolean | null | undefined,
  orgEnabled: boolean | null | undefined
): boolean {
  if (orgLockState === 'managed' && teamEnabled === null) {
    return orgEnabled ?? true;
  }
  return teamEnabled ?? true;
}

/**
 * True when the org pins a value, so team admins cannot change it.
 *
 * `managed` is an org *default* rather than a mandate and so is deliberately not
 * pinned: the team API accepts a team switching such a source off, and locking it
 * here would strip that granularity from team admins.
 */
export function isOrgValuePinned(orgLockState: OrgEvidenceSourceLockState): boolean {
  return orgLockState === 'disabled' || orgLockState === 'forced_on';
}
